import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import { rollDaily, rollWeekly } from '../bounties/dayCycle';
import { RIFT } from './rules';

/**
 * Daily and weekly lockouts (research 02 §5.3, §9.13), stored on the
 * character save that the day cycle rolls over (`char.daily`, `char.weekly`):
 * - Rift Delve: 50 Rift Energy per rewarded run (spent on entry, as in the
 *   original: leaving early forfeits it); without energy a run is practice.
 * - Colossus Hunt: one reward claim per day (`daily.colossusClaimed`).
 * - Deepvault / Warlord Siege: each gate pays once per week; gates are done
 *   in order but may be split across groups. The Siege difficulty is locked
 *   by the first gate cleared that week (`weekly.siegeMode`) and Normal/Hard
 *   share the gate lockout.
 */

export type Raid = 'deepvault' | 'siege';
export type SiegeMode = 'normal' | 'hard';

export const RAID_GATES: Record<Raid, number> = { deepvault: 2, siege: 3 };

/** Rolls stale day/week records (a no-op when the day cycle already did). */
export function ensureCurrent(c: CharacterSave, a: AccountSave): void {
  rollDaily(c, a);
  rollWeekly(c, a);
}

const dailyFresh = (c: CharacterSave, a: AccountSave): boolean => c.daily.day === a.day;
const weeklyFresh = (c: CharacterSave, a: AccountSave): boolean => c.weekly.week === a.week;

// ---------------------------------------------------------------- Rift Delve

export function riftEnergy(c: CharacterSave, a: AccountSave): number {
  return dailyFresh(c, a) ? Math.max(0, c.daily.riftEnergy) : 100;
}

/** Spends one run's Rift Energy. False (practice run) when there is not enough. */
export function spendRiftEnergy(c: CharacterSave, a: AccountSave): boolean {
  ensureCurrent(c, a);
  if (c.daily.riftEnergy < RIFT.energyCost) return false;
  c.daily.riftEnergy -= RIFT.energyCost;
  return true;
}

/** Rested points banked for the Rift (20 double one run's rewards). */
export function riftRested(c: CharacterSave): number {
  return c.daily.riftRested;
}

// ---------------------------------------------------------------- Colossus Hunt

export function colossusClaimed(c: CharacterSave, a: AccountSave): boolean {
  return dailyFresh(c, a) && c.daily.colossusClaimed;
}

/** Marks today's Colossus claim; false if it was already used. */
export function claimColossus(c: CharacterSave, a: AccountSave): boolean {
  ensureCurrent(c, a);
  if (c.daily.colossusClaimed) return false;
  c.daily.colossusClaimed = true;
  return true;
}

// ---------------------------------------------------------------- weekly gates

/** Gates of a raid cleared this week (sorted). */
export function gatesCleared(c: CharacterSave, a: AccountSave, raid: Raid): number[] {
  if (!weeklyFresh(c, a)) return [];
  return [...(raid === 'deepvault' ? c.weekly.deepvault : c.weekly.siege)].sort((x, y) => x - y);
}

/** The first gate not yet cleared this week, or null when the raid is locked out. */
export function nextGate(c: CharacterSave, a: AccountSave, raid: Raid): number | null {
  const done = gatesCleared(c, a, raid);
  for (let g = 1; g <= RAID_GATES[raid]; g++) if (!done.includes(g)) return g;
  return null;
}

/** Difficulty the Siege is locked to this week (null until a gate is cleared). */
export function siegeMode(c: CharacterSave, a: AccountSave): SiegeMode | null {
  return weeklyFresh(c, a) ? c.weekly.siegeMode : null;
}

export type GateClaim = 'ok' | 'cleared' | 'mode';

/** Whether clearing `gate` would pay out this week. */
export function gateClaim(c: CharacterSave, a: AccountSave, raid: Raid, gate: number, mode: SiegeMode = 'normal'): GateClaim {
  if (gatesCleared(c, a, raid).includes(gate)) return 'cleared';
  if (raid === 'siege') {
    const locked = siegeMode(c, a);
    if (locked && locked !== mode) return 'mode';
  }
  return 'ok';
}

/** Records a gate clear for the week. Returns the claim result (only 'ok' pays rewards). */
export function recordGate(c: CharacterSave, a: AccountSave, raid: Raid, gate: number, mode: SiegeMode = 'normal'): GateClaim {
  ensureCurrent(c, a);
  const claim = gateClaim(c, a, raid, gate, mode);
  if (claim !== 'ok') return claim;
  if (raid === 'deepvault') c.weekly.deepvault = [...c.weekly.deepvault, gate];
  else {
    c.weekly.siege = [...c.weekly.siege, gate];
    c.weekly.siegeMode = mode;
  }
  return 'ok';
}
